import { useEffect, useState, useMemo } from "react";
import { Table, Spinner, Button } from "reactstrap";
import { saveAs } from "file-saver";
import { getVacancyLeaseExpiryReport, exportVacancyLeaseExpiryExcel } from "../TenantForm/Helpers/api";
import "./vacancyLeaseExpiry.css";

const STATUS = {
  VACANT: { bg: "#fff8e6", fg: "#9a6700", dot: "#caa12b", label: "Vacant" },
  EXPIRED: { bg: "#fdeaea", fg: "#b3261e", dot: "#d9534f", label: "Expired" },
  EXPIRING_30: { bg: "#fdeaea", fg: "#b3261e", dot: "#d9534f", label: "Expiring ≤ 30 days" },
  EXPIRING_60: { bg: "#fff1e6", fg: "#a8530c", dot: "#e08a3c", label: "Expiring 31–60 days" },
  EXPIRING_90: { bg: "#e8f0fe", fg: "#1a56c4", dot: "#3b7ddd", label: "Expiring 61–90 days" },
  ACTIVE: { bg: "#e6f4ea", fg: "#1e7e45", dot: "#34a853", label: "Active (> 90 days)" },
  NO_END_DATE: { bg: "#eef0f2", fg: "#5b6670", dot: "#9aa3ab", label: "No lease end date" },
};

const Chip = ({ bg, fg, dot, children, title }) => (
  <span
    title={title}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: bg,
      color: fg,
      borderRadius: 999,
      padding: "3px 10px",
      fontSize: "0.72rem",
      fontWeight: 600,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
    }}
  >
    {dot && (
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0 }} />
    )}
    {children}
  </span>
);

const COVER = {
  SIGNED: { label: "Signed — awaiting activation", short: "Signed" },
  RENEWAL_IN_PROGRESS: { label: "Renewal in progress", short: "Renewing" },
};

const FILTERS = [
  { value: "ALL", label: "All units" },
  { value: "VACANT", label: "Vacant only" },
  { value: "EXPIRING_30", label: "Expiring ≤ 30 days" },
  { value: "EXPIRING_60", label: "Expiring 31–60 days" },
  { value: "EXPIRING_90", label: "Expiring 61–90 days" },
  { value: "EXPIRED", label: "Expired" },
  { value: "NO_END_DATE", label: "No lease date entered" },
  { value: "COVERED", label: "Arranged (renewal/signed)" },
  { value: "UNCOVERED", label: "Needs attention (not arranged)" },
];

/** One place both the table and every filter's count read the rules from. */
const matchesFilter = (row, value) => {
  if (value === "ALL") return true;
  if (value === "COVERED") return !!row.covered;
  if (value === "UNCOVERED") {
    return !row.covered && ["VACANT", "EXPIRED", "EXPIRING_30", "EXPIRING_60", "EXPIRING_90"].includes(row.status);
  }
  return row.status === value;
};

const ROW_CLASS = {
  VACANT: "table-warning",
  EXPIRED: "table-danger",
  EXPIRING_30: "table-danger",
};

const TONE = {
  neutral: { bg: "#ffffff", border: "#e2e6ea", accent: "#334155" },
  amber: { bg: "#fff8e6", border: "#f5d990", accent: "#9a6700" },
  red: { bg: "#fdeaea", border: "#f3b6b6", accent: "#b3261e" },
  orange: { bg: "#fff1e6", border: "#f6c89a", accent: "#a8530c" },
  blue: { bg: "#e8f0fe", border: "#aac4f5", accent: "#1a56c4" },
  green: { bg: "#e6f4ea", border: "#7bc996", accent: "#1e7e45" },
};

const StatCard = ({ label, count, covered = 0, breakdown, tone = "neutral", showCoverage = false }) => {
  const fullyCovered = showCoverage && count > 0 && covered >= count;
  const t = fullyCovered ? TONE.green : TONE[tone];
  return (
    <div
      style={{
        flex: "1 1 150px",
        minWidth: 150,
        border: `1px solid ${t.border}`,
        background: t.bg,
        borderRadius: 10,
        padding: "10px 14px",
      }}
    >
      <div className="small text-muted" style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: t.accent, lineHeight: 1.2 }}>
        {breakdown || count}
      </div>
      {showCoverage && count > 0 && (
        <div className="small" style={{ color: fullyCovered ? TONE.green.accent : "#6c757d", fontWeight: 600 }}>
          {fullyCovered ? (
            <>✓ all {count} arranged</>
          ) : (
            <>{covered} of {count} arranged</>
          )}
        </div>
      )}
    </div>
  );
};

const VacancyLeaseExpiryTab = ({ masterSheetData, headerTiles = null }) => {
  const mastersheetId = masterSheetData?._id;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    getVacancyLeaseExpiryReport(mastersheetId)
      .then((resp) => {
        if (!active) return;
        if (resp?.success) setReport(resp.data);
        else setError(resp?.message || "Failed to load report");
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.message || "Failed to load report");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mastersheetId]);

  const visibleRows = useMemo(
    () => (report?.rows || []).filter((r) => matchesFilter(r, statusFilter)),
    [report, statusFilter]
  );

  // Every option carries how many units it would show, so the count is visible before
  // you pick the filter rather than after.
  const filterCounts = useMemo(() => {
    const rows = report?.rows || [];
    return FILTERS.reduce((acc, f) => ({ ...acc, [f.value]: rows.filter((r) => matchesFilter(r, f.value)).length }), {});
  }, [report]);

  // What the foot of the table reports — computed over the visible rows, never the
  // whole set, so filtering the table filters the numbers under it too.
  const shownCounts = useMemo(() => ({
    vacant: visibleRows.filter((r) => r.status === "VACANT").length,
    noEndDate: visibleRows.filter((r) => r.status === "NO_END_DATE").length,
    arranged: visibleRows.filter((r) => r.covered).length,
    needsAttention: visibleRows.filter(
      (r) => !r.covered && ["VACANT", "EXPIRED", "EXPIRING_30", "EXPIRING_60", "EXPIRING_90"].includes(r.status)
    ).length,
  }), [visibleRows]);

  const handleDownload = async () => {
    if (!mastersheetId) return;
    setDownloading(true);
    try {
      const blob = await exportVacancyLeaseExpiryExcel(mastersheetId);
      const safe = (report?.building_name || "report").replace(/[^a-z0-9_-]+/gi, "_");
      saveAs(blob, `Vacancy-LeaseExpiry_${safe}.xlsx`);
    } catch (e) {
      setError("Failed to download Excel.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center p-4">
        <Spinner color="primary" /> Building report…
      </div>
    );
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!report) return <p className="text-muted text-center">No data available.</p>;

  const t = report.totals;
  const b = (x) => (x && typeof x === "object" ? x : { count: x || 0, covered: 0 });
  const vac = b(t.vacant);
  const e30 = b(t.expiring30);
  const e60 = b(t.expiring60);
  const e90 = b(t.expiring90);
  const band3160 = b(t.band31to60);
  const band6190 = b(t.band61to90);
  const expd = b(t.expired);

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {headerTiles}
        <StatCard label="Total units" count={t.unitsTotal} tone="neutral" />
        <StatCard label="Vacant units" count={vac.count} covered={vac.covered} tone="amber" showCoverage />
        <StatCard label="Expiring in 30 days" count={e30.count} covered={e30.covered} tone="red" showCoverage />
        <StatCard
          label="Expiring in 60 days"
          count={e60.count}
          covered={e60.covered}
          breakdown={`${e30.count} + ${band3160.count} = ${e60.count}`}
          tone="orange"
          showCoverage
        />
        <StatCard
          label="Expiring in 90 days"
          count={e90.count}
          covered={e90.covered}
          breakdown={`${e60.count} + ${band6190.count} = ${e90.count}`}
          tone="blue"
          showCoverage
        />
        <StatCard label="Already expired" count={expd.count} covered={expd.covered} tone="red" showCoverage />
        <StatCard label="Active (> 90 days)" count={t.active} tone="green" />
        <StatCard label="No lease end date" count={t.noEndDate} tone="neutral" />
      </div>

      <p className="small text-muted mb-2">
        60 &amp; 90-day boxes are cumulative (e.g. <em>30-day + next band = total</em>). “Arranged” counts units whose
        next tenancy is already a renewal-in-progress or a signed lease awaiting activation; a box turns
        <span style={{ color: TONE.green.accent, fontWeight: 600 }}> green</span> once every expiring/vacant unit in it is arranged.
      </p>

      <div className="d-flex align-items-center mb-2">
        <select
          className="form-select form-select-sm w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label} ({filterCounts[f.value] ?? 0})
            </option>
          ))}
        </select>
        <Button color="success" className="ms-auto" onClick={handleDownload} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <Table hover responsive className="table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th>Unit #</th>
            <th>Tenant</th>
            <th>Lease Start</th>
            <th>Lease End</th>
            <th className="text-end">Days Left</th>
            <th>Status</th>
            <th>Next Tenancy</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length ? (
            visibleRows.map((r, i) => (
              <tr key={`${r.unitNo}-${i}`} className={r.covered ? "table-success" : ROW_CLASS[r.status] || ""}>
                <td>
                  <strong>{r.unitNo}</strong>
                </td>
                <td>{r.tenant}</td>
                <td>{r.leaseStart || "—"}</td>
                <td>{r.leaseEnd || "—"}</td>
                <td className="text-end">{r.daysUntilExpiry === null ? "—" : r.daysUntilExpiry}</td>
                <td>
                  <Chip
                    bg={(STATUS[r.status] || STATUS.NO_END_DATE).bg}
                    fg={(STATUS[r.status] || STATUS.NO_END_DATE).fg}
                    dot={(STATUS[r.status] || STATUS.NO_END_DATE).dot}
                  >
                    {STATUS[r.status]?.label ?? r.status}
                  </Chip>
                </td>
                <td>
                  {r.coverState ? (
                    <Chip bg="#e6f4ea" fg="#1e7e45" dot="#34a853" title={COVER[r.coverState]?.label}>
                      {COVER[r.coverState]?.short ?? "Arranged"}
                    </Chip>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No units to display.
              </td>
            </tr>
          )}
        </tbody>
        {/* The numbers for what is actually on screen, at the foot of the table —
            they follow the filter, so the count always describes these rows. */}
        <tfoot>
          <tr className="vle-total">
            <td colSpan={5}>
              Total rows: <strong>{visibleRows.length}</strong>
              {statusFilter !== "ALL" && (
                <span className="fw-normal text-muted"> of {report.rows.length} units</span>
              )}
            </td>
            <td>
              {shownCounts.vacant} vacant · {shownCounts.noEndDate} no lease date
            </td>
            <td>
              {shownCounts.arranged} arranged
              {shownCounts.needsAttention > 0 && (
                <span className="fw-normal text-muted"> · {shownCounts.needsAttention} need attention</span>
              )}
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

export { VacancyLeaseExpiryTab, StatCard, TONE };
